package org.catharsis.spendtel.feature.data

import kotlinx.serialization.Serializable

@Serializable
data class ExpenseReportDocumentDto(
    val status: String = "processing",
    val createdAt: Long? = null,
    val updatedAt: Long? = null,
    val sourceDocuments: List<SourceDocument> = emptyList(),
    val error: String? = null,
    val summary: ReportSummary? = null,
    val monthlyStory: MonthlyStory? = null,
    val lifeCategories: List<LifeCategory> = emptyList(),
    val financialPersonality: FinancialPersonality? = null,
    val merchantInsights: List<MerchantInsight> = emptyList(),
    val spendingTimeline: List<TimelineEntry> = emptyList(),
    val whatChanged: WhatChanged? = null,
    val aboutYou: List<String> = emptyList(),
    val spendingStories: List<SpendingStory> = emptyList(),
    val transactions: List<Transaction> = emptyList(),
    val financialGoalUsed: String? = null,
    val equivalences: List<SpendingEquivalence> = emptyList(),
    val goalInsights: List<GoalInsight> = emptyList(),
    val spendingPatterns: SpendingPatterns? = null,
    val trends: ReportTrends? = null,
    val moneyLeaks: MoneyLeaks? = null,
    val highlights: List<String> = emptyList(),
)

fun ExpenseReportDocumentDto.toExpenseReport(id: String): ExpenseReport {
    return ExpenseReport(
        id = id,
        status = status,
        createdAtMillis = createdAt ?: 0L,
        sourceDocuments = sourceDocuments,
        error = error,
        summary = summary,
        monthlyStory = monthlyStory,
        lifeCategories = lifeCategories,
        financialPersonality = financialPersonality,
        merchantInsights = merchantInsights,
        spendingTimeline = spendingTimeline,
        whatChanged = whatChanged,
        aboutYou = aboutYou,
        spendingStories = spendingStories,
        transactions = transactions,
        financialGoalUsed = financialGoalUsed,
        equivalences = equivalences,
        goalInsights = goalInsights,
        spendingPatterns = spendingPatterns,
        trends = trends,
        moneyLeaks = moneyLeaks,
        highlights = highlights,
    )
}

data class ExpenseReport(
    val id: String,
    val status: String,
    val createdAtMillis: Long,
    val sourceDocuments: List<SourceDocument> = emptyList(),
    val error: String? = null,
    val summary: ReportSummary? = null,
    val monthlyStory: MonthlyStory? = null,
    val lifeCategories: List<LifeCategory> = emptyList(),
    val financialPersonality: FinancialPersonality? = null,
    val merchantInsights: List<MerchantInsight> = emptyList(),
    val spendingTimeline: List<TimelineEntry> = emptyList(),
    val whatChanged: WhatChanged? = null,
    val aboutYou: List<String> = emptyList(),
    val spendingStories: List<SpendingStory> = emptyList(),
    val transactions: List<Transaction> = emptyList(),
    val financialGoalUsed: String? = null,
    val equivalences: List<SpendingEquivalence> = emptyList(),
    val goalInsights: List<GoalInsight> = emptyList(),
    val spendingPatterns: SpendingPatterns? = null,
    val trends: ReportTrends? = null,
    val moneyLeaks: MoneyLeaks? = null,
    val highlights: List<String> = emptyList(),
) {
    val sourceDocumentCount: Int
        get() = sourceDocuments.size

    val statementPeriodLabel: String
        get() = statementPeriodLabelForReport(
            periodStart = summary?.periodStart,
            periodEnd = summary?.periodEnd,
            createdAtMillis = createdAtMillis,
            isProcessing = isProcessing,
        )

    val displayLabel: String
        get() {
            val statementLabel = when (sourceDocumentCount) {
                0 -> "No statements"
                1 -> "1 statement"
                else -> "$sourceDocumentCount statements"
            }
            return "$statementPeriodLabel · $statementLabel"
        }

    val statusLabel: String
        get() = ExpenseReportStatus.displayLabel(status)

    val isProcessing: Boolean
        get() = ExpenseReportStatus.isProcessing(status)

    val isComplete: Boolean
        get() = ExpenseReportStatus.isComplete(status)

    val isFailed: Boolean
        get() = ExpenseReportStatus.isFailed(status)

    val hasConversationalContent: Boolean
        get() = monthlyStory != null ||
            financialPersonality != null ||
            hasExtendedInsights

    val hasExtendedInsights: Boolean
        get() = equivalences.isNotEmpty() ||
            goalInsights.isNotEmpty() ||
            spendingPatterns != null ||
            trends != null ||
            moneyLeaks != null ||
            highlights.isNotEmpty()

    val hasComparisonContent: Boolean
        get() = whatChanged != null || trends != null

    fun dashboardCurrency(): String = summary?.currency ?: "USD"
}

object ExpenseReportStatus {
    fun isProcessing(status: String): Boolean {
        return status.lowercase() in PROCESSING_STATUSES
    }

    fun isComplete(status: String): Boolean {
        return status.lowercase() in COMPLETE_STATUSES
    }

    fun isFailed(status: String): Boolean {
        return status.lowercase() in FAILED_STATUSES
    }

    fun isTerminal(status: String): Boolean {
        return isComplete(status) || isFailed(status)
    }

    fun displayLabel(status: String): String {
        return when (status.lowercase()) {
            "complete", "completed", "ready" -> "Complete"
            "failed", "error" -> "Failed"
            "parsing" -> "Analyzing"
            "documents_loaded" -> "Loading"
            "processing", "pending", "in_progress" -> "Processing"
            else -> status.replace('_', ' ').replaceFirstChar { char ->
                if (char.isLowerCase()) char.titlecase() else char.toString()
            }
        }
    }

    private val PROCESSING_STATUSES = setOf(
        "processing",
        "pending",
        "in_progress",
        "documents_loaded",
        "parsing",
    )
    private val COMPLETE_STATUSES = setOf("complete", "completed", "ready")
    private val FAILED_STATUSES = setOf("failed", "error")
}

fun ExpenseReportResponse.toExpenseReport(createdAtMillis: Long): ExpenseReport {
    return ExpenseReport(
        id = reportId,
        status = status,
        createdAtMillis = createdAtMillis,
        sourceDocuments = sourceDocuments.orEmpty(),
        error = error,
        summary = summary,
        monthlyStory = monthlyStory,
        lifeCategories = lifeCategories.orEmpty(),
        financialPersonality = financialPersonality,
        merchantInsights = merchantInsights.orEmpty(),
        spendingTimeline = spendingTimeline.orEmpty(),
        whatChanged = whatChanged,
        aboutYou = aboutYou.orEmpty(),
        spendingStories = spendingStories.orEmpty(),
        transactions = transactions.orEmpty(),
        financialGoalUsed = financialGoalUsed,
        equivalences = equivalences.orEmpty(),
        goalInsights = goalInsights.orEmpty(),
        spendingPatterns = spendingPatterns,
        trends = trends,
        moneyLeaks = moneyLeaks,
        highlights = highlights.orEmpty(),
    )
}
